import React, { useState, useEffect } from 'react';
import {
  Form,
  Input,
  Button,
  Space,
  Card,
  Row,
  Col,
  Select,
  Switch,
  Divider,
  Typography,
  message,
  Table,
  Modal,
  Popconfirm,
  Tag,
  Tooltip
} from 'antd';
import {
  PlusOutlined,
  DeleteOutlined,
  EditOutlined,
  InfoCircleOutlined
} from '@ant-design/icons';
import { documentTypesAPI } from '../services/api';

const { Title, Text } = Typography;
const { TextArea } = Input;
const { Option } = Select;

interface Field {
  name: string;
  type: 'string' | 'number' | 'date' | 'boolean' | 'email' | 'phone' | 'currency';
  required: boolean;
  description?: string;
  validation_pattern?: string;
  extraction_rules?: {
    patterns?: string[];
    keywords?: string[];
    region?: { x: number; y: number; width: number; height: number };
  };
}

interface DocumentTypeEditorProps {
  documentType?: any;
  isCreating: boolean;
  onSuccess: () => void;
  onCancel: () => void;
}

const DocumentTypeEditor: React.FC<DocumentTypeEditorProps> = ({
  documentType,
  isCreating,
  onSuccess,
  onCancel
}) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [fields, setFields] = useState<Field[]>([]);
  const [fieldModalVisible, setFieldModalVisible] = useState(false);
  const [editingField, setEditingField] = useState<Field | null>(null);
  const [editingFieldIndex, setEditingFieldIndex] = useState<number>(-1);

  // Initialize form data
  useEffect(() => {
    if (documentType && !isCreating) {
      form.setFieldsValue({
        document_type: documentType.document_type,
        description: documentType.description
      });
      setFields(documentType.schema?.fields || []);
    } else {
      // Reset form for new document type
      form.resetFields();
      setFields([]);
    }
  }, [documentType, isCreating, form]);

  // Handle form submission
  const handleSubmit = async (values: any) => {
    if (fields.length === 0) {
      message.error('Please add at least one field to the schema');
      return;
    }

    try {
      setLoading(true);
      
      const schemaData = {
        document_type: values.document_type,
        description: values.description,
        schema: {
          version: isCreating ? '1.0.0' : documentType.schema.version,
          fields: fields
        }
      };

      let response;
      if (isCreating) {
        response = await documentTypesAPI.createDocumentType(schemaData);
      } else {
        response = await documentTypesAPI.updateDocumentType(
          documentType.document_type,
          schemaData
        );
      }

      if (response.data.success) {
        message.success(
          `Document type ${isCreating ? 'created' : 'updated'} successfully`
        );
        onSuccess();
      }
    } catch (error) {
      console.error('Failed to save document type:', error);
      message.error(`Failed to ${isCreating ? 'create' : 'update'} document type`);
    } finally {
      setLoading(false);
    }
  };

  // Add new field
  const handleAddField = () => {
    setEditingField(null);
    setEditingFieldIndex(-1);
    setFieldModalVisible(true);
  };

  // Edit existing field
  const handleEditField = (field: Field, index: number) => {
    setEditingField(field);
    setEditingFieldIndex(index);
    setFieldModalVisible(true);
  };

  // Delete field
  const handleDeleteField = (index: number) => {
    const newFields = fields.filter((_, i) => i !== index);
    setFields(newFields);
  };

  // Save field (add or update)
  const handleSaveField = (fieldData: Field) => {
    if (editingFieldIndex >= 0) {
      // Update existing field
      const newFields = [...fields];
      newFields[editingFieldIndex] = fieldData;
      setFields(newFields);
    } else {
      // Add new field
      setFields([...fields, fieldData]);
    }
    setFieldModalVisible(false);
  };

  // Field table columns
  const fieldColumns = [
    {
      title: 'Field Name',
      dataIndex: 'name',
      key: 'name',
      render: (text: string) => <Text strong>{text}</Text>
    },
    {
      title: 'Type',
      dataIndex: 'type',
      key: 'type',
      render: (type: string) => {
        const colors: { [key: string]: string } = {
          string: 'blue',
          number: 'green',
          date: 'purple',
          boolean: 'orange',
          email: 'cyan',
          phone: 'magenta',
          currency: 'gold'
        };
        return <Tag color={colors[type] || 'default'}>{type}</Tag>;
      }
    },
    {
      title: 'Required',
      dataIndex: 'required',
      key: 'required',
      render: (required: boolean) => (
        <Tag color={required ? 'red' : 'default'}>
          {required ? 'Required' : 'Optional'}
        </Tag>
      )
    },
    {
      title: 'Description',
      dataIndex: 'description',
      key: 'description',
      ellipsis: true,
      render: (text: string) => text || <Text type="secondary">No description</Text>
    },
    {
      title: 'Validation',
      dataIndex: 'validation_pattern',
      key: 'validation_pattern',
      ellipsis: true,
      render: (pattern: string) => pattern ? (
        <Tooltip title={pattern}>
          <Tag color="purple">Has Pattern</Tag>
        </Tooltip>
      ) : <Text type="secondary">None</Text>
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 120,
      render: (_: any, record: Field, index: number) => (
        <Space size="small">
          <Tooltip title="Edit">
            <Button
              type="text"
              icon={<EditOutlined />}
              onClick={() => handleEditField(record, index)}
            />
          </Tooltip>
          <Popconfirm
            title="Are you sure you want to delete this field?"
            onConfirm={() => handleDeleteField(index)}
            okText="Yes"
            cancelText="No"
          >
            <Tooltip title="Delete">
              <Button
                type="text"
                danger
                icon={<DeleteOutlined />}
              />
            </Tooltip>
          </Popconfirm>
        </Space>
      )
    }
  ];

  return (
    <div>
      <Form
        form={form}
        layout="vertical"
        onFinish={handleSubmit}
      >
        <Card title="Basic Information" style={{ marginBottom: '16px' }}>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="document_type"
                label="Document Type"
                rules={[
                  { required: true, message: 'Please enter document type name' },
                  { pattern: /^[a-zA-Z0-9_-]+$/, message: 'Only letters, numbers, underscores, and hyphens are allowed' }
                ]}
              >
                <Input
                  placeholder="e.g., invoice, receipt, contract"
                  disabled={!isCreating}
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="description"
                label="Description"
                rules={[{ required: true, message: 'Please enter description' }]}
              >
                <Input placeholder="Brief description of this document type" />
              </Form.Item>
            </Col>
          </Row>
        </Card>

        <Card 
          title="Schema Fields" 
          style={{ marginBottom: '16px' }}
          extra={
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={handleAddField}
            >
              Add Field
            </Button>
          }
        >
          <Table
            columns={fieldColumns}
            dataSource={fields}
            rowKey="name"
            pagination={false}
            size="small"
            locale={{
              emptyText: 'No fields defined. Click "Add Field" to get started.'
            }}
          />
        </Card>

        <Space style={{ width: '100%', justifyContent: 'flex-end' }}>
          <Button onClick={onCancel}>
            Cancel
          </Button>
          <Button type="primary" htmlType="submit" loading={loading}>
            {isCreating ? 'Create' : 'Update'} Document Type
          </Button>
        </Space>
      </Form>

      {/* Field Editor Modal */}
      <FieldEditor
        visible={fieldModalVisible}
        field={editingField}
        onSave={handleSaveField}
        onCancel={() => setFieldModalVisible(false)}
        existingFieldNames={fields.map(f => f.name)}
      />
    </div>
  );
};

// Field Editor Component
interface FieldEditorProps {
  visible: boolean;
  field?: Field | null;
  onSave: (field: Field) => void;
  onCancel: () => void;
  existingFieldNames: string[];
}

const FieldEditor: React.FC<FieldEditorProps> = ({
  visible,
  field,
  onSave,
  onCancel,
  existingFieldNames
}) => {
  const [form] = Form.useForm();
  const [patterns, setPatterns] = useState<string[]>([]);
  const [keywords, setKeywords] = useState<string[]>([]);

  useEffect(() => {
    if (visible) {
      if (field) {
        // Edit mode
        form.setFieldsValue({
          name: field.name,
          type: field.type,
          required: field.required,
          description: field.description || '',
          validation_pattern: field.validation_pattern || ''
        });
        setPatterns(field.extraction_rules?.patterns || []);
        setKeywords(field.extraction_rules?.keywords || []);
      } else {
        // Create mode
        form.resetFields();
        setPatterns([]);
        setKeywords([]);
      }
    }
  }, [visible, field, form]);

  const handleSave = async () => {
    try {
      const values = await form.validateFields();
      
      const fieldData: Field = {
        name: values.name,
        type: values.type,
        required: values.required,
        description: values.description,
        validation_pattern: values.validation_pattern,
        extraction_rules: {
          patterns: patterns.length > 0 ? patterns : undefined,
          keywords: keywords.length > 0 ? keywords : undefined
        }
      };

      onSave(fieldData);
    } catch (error) {
      // Form validation failed
    }
  };

  return (
    <Modal
      title={field ? 'Edit Field' : 'Add Field'}
      open={visible}
      onOk={handleSave}
      onCancel={onCancel}
      width={600}
      destroyOnClose
    >
      <Form
        form={form}
        layout="vertical"
        initialValues={{
          required: false,
          type: 'string'
        }}
      >
        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              name="name"
              label="Field Name"
              rules={[
                { required: true, message: 'Please enter field name' },
                { pattern: /^[a-zA-Z0-9_]+$/, message: 'Only letters, numbers, and underscores allowed' },
                {
                  validator: (_, value) => {
                    if (value && existingFieldNames.includes(value) && (!field || field.name !== value)) {
                      return Promise.reject('Field name already exists');
                    }
                    return Promise.resolve();
                  }
                }
              ]}
            >
              <Input placeholder="e.g., invoice_number, total_amount" />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              name="type"
              label="Field Type"
              rules={[{ required: true, message: 'Please select field type' }]}
            >
              <Select>
                <Option value="string">Text/String</Option>
                <Option value="number">Number</Option>
                <Option value="date">Date</Option>
                <Option value="boolean">Boolean</Option>
                <Option value="email">Email</Option>
                <Option value="phone">Phone</Option>
                <Option value="currency">Currency</Option>
              </Select>
            </Form.Item>
          </Col>
        </Row>

        <Form.Item
          name="description"
          label="Description"
        >
          <TextArea
            rows={2}
            placeholder="Brief description of this field"
          />
        </Form.Item>
        
        <Form.Item
          label={
            <Space>
              Extraction Rules
              <Tooltip title="Rules to extract this field from the document">
                <InfoCircleOutlined style={{ color: '#999' }} />
              </Tooltip>
            </Space>
          }
        >
          <Input
            placeholder="Extract full_name from the document"
            value={patterns[0] || ''}
            onChange={(e) => setPatterns(e.target.value ? [e.target.value] : [])}
          />
          <Text type="secondary" style={{ fontSize: '12px' }}>
            Example: Extract field_name from the document
          </Text>
        </Form.Item>

        <Form.Item
          name="required"
          label="Required Field"
          valuePropName="checked"
        >
          <Switch />
        </Form.Item>

        <Divider>Optional Settings</Divider>

        <Form.Item
          name="validation_pattern"
          label={
            <Space>
              Validation Pattern (Regex)
              <Tooltip title="Regular expression to validate field values">
                <InfoCircleOutlined style={{ color: '#999' }} />
              </Tooltip>
            </Space>
          }
        >
          <Input 
            placeholder="e.g., ^\d{3}-\d{2}-\d{4}$ for SSN format"
          />
        </Form.Item>

        <Form.Item
          label={
            <Space>
              Keywords
              <Tooltip title="Keywords to look for when extracting this field">
                <InfoCircleOutlined style={{ color: '#999' }} />
              </Tooltip>
            </Space>
          }
        >
          <Select
            mode="tags"
            style={{ width: '100%' }}
            placeholder="Enter keywords..."
            value={keywords}
            onChange={setKeywords}
            tokenSeparators={[',']}
          />
          <Text type="secondary" style={{ fontSize: '12px' }}>
            Example: total, amount, sum, grand total
          </Text>
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default DocumentTypeEditor;
